/**
 * WebSocketNotifications Component
 *
 * A compound component that provides:
 * 1. WebSocket connection management based on current terreiro
 * 2. NotificationBell with real-time notification count
 * 3. Connection status indicator (only shows when disconnected)
 * 4. Toast notifications for new requests
 *
 * Usage:
 * ```tsx
 * <WebSocketNotifications>
 *   <WebSocketNotifications.Bell />
 *   <WebSocketNotifications.Status />
 *   <WebSocketNotifications.Toasts />
 * </WebSocketNotifications>
 * ```
 *
 * Or use all-in-one:
 * ```tsx
 * <WebSocketNotifications.All />
 * ```
 */
import { Alert, Snackbar } from "@mui/material";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type { RequestNotification } from "../../hooks/useRequestNotifications";
import {
  useWebSocketIntegration,
  type UseWebSocketIntegrationReturn,
} from "../../hooks/useWebSocketIntegration";
import { ConnectionStatusIndicator } from "../ConnectionStatusIndicator";
import { NotificationBell } from "../NotificationBell";

// Context
const WebSocketNotificationsContext =
  createContext<UseWebSocketIntegrationReturn | null>(null);

// Hook to access context
// eslint-disable-next-line react-refresh/only-export-components
export function useWebSocketNotificationsContext() {
  const context = useContext(WebSocketNotificationsContext);
  if (!context) {
    throw new Error(
      "useWebSocketNotificationsContext must be used within WebSocketNotifications",
    );
  }
  return context;
}

// Main Provider Component
interface WebSocketNotificationsProps {
  children: React.ReactNode;
  /** Enable/disable WebSocket connection */
  enabled?: boolean;
  /** Custom callback for new notifications */
  onNotification?: (notification: RequestNotification) => void;
}

function WebSocketNotificationsProvider({
  children,
  enabled = true,
  onNotification,
}: WebSocketNotificationsProps) {
  const integration = useWebSocketIntegration({
    enabled,
    onNotification,
  });

  return (
    <WebSocketNotificationsContext.Provider value={integration}>
      {children}
    </WebSocketNotificationsContext.Provider>
  );
}

// Bell Component
interface BellProps {
  /** Maximum notifications to show in dropdown */
  maxVisible?: number;
  /** Whether to animate when there are unread notifications */
  animate?: boolean;
}

function Bell({ maxVisible = 5, animate = true }: BellProps) {
  const context = useWebSocketNotificationsContext();

  return (
    <NotificationBell
      notifications={context.notifications}
      unreadCount={context.unreadCount}
      onNotificationClick={context.navigateToNotification}
      onMarkAsRead={context.markAsRead}
      onMarkAllAsRead={context.markAllAsRead}
      onViewAll={context.navigateToNotifications}
      onViewUnread={context.navigateToUnreadNotifications}
      animate={animate}
      maxVisible={maxVisible}
    />
  );
}

// Status Indicator Component
interface StatusProps {
  /** Always show indicator (even when connected) */
  alwaysShow?: boolean;
  /** Size variant */
  size?: "small" | "medium";
  /** Whether to show as chip or just icon */
  variant?: "chip" | "icon";
}

function Status({
  alwaysShow = false,
  size = "small",
  variant = "icon",
}: StatusProps) {
  const context = useWebSocketNotificationsContext();

  return (
    <ConnectionStatusIndicator
      state={context.connectionState}
      reconnectAttempts={context.reconnectAttempts}
      maxReconnectAttempts={10}
      alwaysShow={alwaysShow}
      size={size}
      variant={variant}
    />
  );
}

// Toast Notifications Component
interface ToastsProps {
  /** Auto-hide duration in milliseconds */
  autoHideDuration?: number;
  /** Position of the toast */
  position?: {
    vertical: "top" | "bottom";
    horizontal: "left" | "center" | "right";
  };
}

function Toasts({
  autoHideDuration = 5000,
  position = { vertical: "top", horizontal: "right" },
}: ToastsProps) {
  const context = useWebSocketNotificationsContext();
  const [open, setOpen] = useState(false);
  const [currentNotification, setCurrentNotification] =
    useState<RequestNotification | null>(null);

  // Show toast when new notification arrives
  useEffect(() => {
    if (context.latestNotification && !context.latestNotification.read) {
      setCurrentNotification(context.latestNotification);
      setOpen(true);
    }
  }, [context.latestNotification]);

  const handleClose = useCallback(
    (_event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === "clickaway") {
        return;
      }
      setOpen(false);
    },
    [],
  );

  const handleClick = useCallback(() => {
    if (currentNotification) {
      context.navigateToNotification(currentNotification);
      setOpen(false);
    }
  }, [currentNotification, context]);

  const getSeverity = () => {
    if (!currentNotification) return "info";
    switch (currentNotification.type) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "created":
      case "updated":
      default:
        return "info";
    }
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
      anchorOrigin={position}
    >
      <Alert
        onClose={handleClose}
        severity={getSeverity()}
        variant="filled"
        onClick={handleClick}
        sx={{ cursor: "pointer", minWidth: 300 }}
      >
        {currentNotification?.message ?? ""}
      </Alert>
    </Snackbar>
  );
}

// All-in-one Component (Bell + Status + Toasts)
interface AllProps extends BellProps, StatusProps, ToastsProps {
  /** Show status indicator */
  showStatus?: boolean;
  /** Show toast notifications */
  showToasts?: boolean;
}

function All({
  // Bell props
  maxVisible = 5,
  animate = true,
  // Status props
  alwaysShow = false,
  size = "small",
  variant = "icon",
  // Toast props
  autoHideDuration = 5000,
  position = { vertical: "top", horizontal: "right" },
  // Toggle props
  showStatus = true,
  showToasts = true,
}: AllProps) {
  return (
    <>
      {showStatus && (
        <Status alwaysShow={alwaysShow} size={size} variant={variant} />
      )}
      <Bell maxVisible={maxVisible} animate={animate} />
      {showToasts && (
        <Toasts autoHideDuration={autoHideDuration} position={position} />
      )}
    </>
  );
}

// Compound Component
export const WebSocketNotifications = Object.assign(
  WebSocketNotificationsProvider,
  {
    Bell,
    Status,
    Toasts,
    All,
    useContext: useWebSocketNotificationsContext,
  },
);

export default WebSocketNotifications;
